import styled from '@emotion/styled';
import DropdownItem from '@app/components/inputs/DropdownItem';
import TooltipUnstyled from '@app/components/TooltipUnstyled';

// White, larger tooltip used for the SWIFT DB bank-name hover on review cells. Built on
// TooltipUnstyled (which resets antd's dark inner box to transparent) so we can restyle the inner
// box to a white card with dark, larger text.
export const BankNameTooltip = styled(TooltipUnstyled)`
  max-width: 240px;

  /* && outranks TooltipUnstyled's .ant-tooltip-inner reset (which sets background: transparent). */
  && .ant-tooltip-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    color: #1c2024;
    font-size: 14px;
    line-height: 1.3;
    padding: 6px 10px;
    border-radius: 6px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
    text-align: center;
  }
`;

export type StatusType = 'Match' | 'PartialMatch' | 'Mismatch' | 'NotRelevant' | 'Unknown' | null;

export type CellVariant =
  | 'noData' // no vendor, no evidence → yellow bg, Search icon
  | 'needsSearch' // has vendor or evidence but no match yet → yellow bg, Search icon
  | 'match' // autoMatch=Match → green bg, CheckCheck icon
  | 'partialMatch' // autoMatch=PartialMatch → green bg, Check icon
  | 'mismatch' // autoMatch=Mismatch → red bg (auto) / yellow bg (review), X icon
  | 'editedManual' // user edited manual in session → orange bg, Info icon
  | 'editedFinal' // approver edited final (hasChangedFromInitial) → dark green bg
  | 'inheritedMatch' // final inherits manual match → green bg, Check icon
  | 'notRelevant' // marked not relevant → gray bg
  | 'empty'; // no data at all → transparent

export interface CellState {
  variant: CellVariant;
  autoMatchIcon: 'checkcheck' | 'check' | 'x' | 'question' | 'dash' | null;
  autoMatchBg: 'green' | 'red' | 'yellow' | 'gray' | 'none';
  reviewBg: 'green' | 'darkGreen' | 'orange' | 'yellow' | 'gray' | 'none';
  reviewIcon: 'search' | 'info' | 'check' | 'question' | null;
}

export const CELL_BG_COLORS: Record<CellState['reviewBg'] | CellState['autoMatchBg'], string> = {
  green: '#e4f5ea',
  darkGreen: '#c8e6c9',
  orange: '#ffddc0',
  yellow: '#faf6df',
  red: '#f5e7e4',
  gray: '#f6f8fd',
  none: 'transparent',
};

export const ExpandableCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ExpandIcon = styled.span<{ expanded: boolean }>`
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transform: ${({ expanded }): string => (expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
  user-select: none;
`;

export const EyeIconWrapper = styled.div`
  width: 16px;
  height: 16px;
  margin-right: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const EvidenceCell = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

export const EvidenceStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

export const EvidenceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const EvidenceValue = styled.span`
  font-size: 14px;
  color: #1c2024;
`;

export const EvidenceSource = styled.span`
  font-size: 14px;
  color: rgba(28, 32, 36, 0.5);
  font-style: italic;
`;

export const EvidenceTypeLabel = styled.span`
  font-size: 13px;
  color: rgba(28, 32, 36, 0.5);
  white-space: nowrap;
  margin-left: auto;
`;

export const ReviewCell = styled.div<{ status?: StatusType; isEdited?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 3px 4px;
  border-radius: 4px;
  background-color: transparent;
`;

export const EditIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.2s;
  margin-left: auto;

  &:hover {
    opacity: 1;
  }
`;

export const ReviewContent = styled.span`
  flex: 1;
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
  word-wrap: break-word;
`;

export const StatusCellWrapper = styled.div<{ status: StatusType }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 100%;
  padding: 4px 12px;
  background-color: transparent;
`;

export const StatusIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const InfoIconContainer = styled.div`
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const DataPointCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const DataPointContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

export const DataPointLabel = styled.span<{ isParent?: boolean }>`
  font-weight: ${({ isParent }): string => (isParent ? '700' : '400')};
  color: #1c2024;
`;

export const DataPointActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

export const Container = styled.div`
  width: 100%;
  padding: 24px;
  background: white;
  box-sizing: border-box;
  min-width: 0;
  // Full-height flex column: the role toggle is a fixed header, MiddleScroll (which now includes the
  // TopSection) takes the remaining space and scrolls, and BottomActions is a fixed footer.
  // height:100% + min-height:0 let the middle band bound itself and scroll instead of the whole tab growing.
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    'Helvetica Neue',
    Arial,
    sans-serif;
`;

export const TopSection = styled.div`
  margin-bottom: 32px;
  padding: 8px 24px;
`;

export const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  margin-bottom: 24px;
  min-width: 800px;
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 220px;
`;

export const AmountInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 140px;
`;

export const RightAlignedGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  padding-top: 13px;
`;

export const StyledInput = styled.input`
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  font-family: 'SF Pro', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: rgba(28, 32, 36, 0.8);
  width: 100%;
  height: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(28, 32, 36, 0.8);
  }

  &:focus {
    outline: none;
  }
`;

export const StyledDateInput = styled.input`
  padding: 0 28px 0 0;
  border: none;
  border-radius: 0;
  background: transparent;
  font-family: 'SF Pro', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: rgba(28, 32, 36, 0.8);
  width: 100%;
  height: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(28, 32, 36, 0.8);
  }

  &:focus {
    outline: none;
  }

  &::-webkit-calendar-picker-indicator {
    opacity: 0;
    position: absolute;
    right: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
`;

export const ClockIcon = styled.span`
  position: absolute;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  width: 16px;
  height: 16px;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const PrivatePayeeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Flow-diff hint, rendered on its own line below a field in the top section / account controls
// (their groups are flex columns, whose gaps provide the spacing). Red so a Manual-vs-Final
// discrepancy is visible at a glance.
export const FlowDiffHintContainer = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: #dc2626;
  font-size: 12px;
  font-weight: 510;
  line-height: 16px;
`;

export const PrivatePayeeLabel = styled.label`
  font-family: 'SF Pro', sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
  letter-spacing: 0px;
  color: #1c2024;
`;

export const PrivateAccountRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SanctionsHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 0;
`;

export const SecondRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
`;

export const TopActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SaveButton = styled.button`
  padding: 7px 20px;
  background: ${(props): string => (props.disabled ? '#e0e0e0' : '#1976d2')};
  color: ${(props): string => (props.disabled ? '#9e9e9e' : '#ffffff')};
  border: none;
  border-radius: 6px;
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    'Helvetica Neue',
    Arial,
    sans-serif;
  font-size: 15px;
  font-weight: 500;
  cursor: ${(props): string => (props.disabled ? 'not-allowed' : 'pointer')};
  height: 38px;
  min-width: 138px;

  &:hover:not(:disabled) {
    background: #1565c0;
  }
`;

export const Label = styled.label`
  font-family: 'SF Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: 0.04px;
  color: #1c2024;
`;

// Note shown under the validation-type dropdown when the account was originally validated in
// Lyons and now carries additional evidence on top of it.
export const OriginallyLyonsNote = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.04px;
  color: #60646c;
  margin-top: 4px;
`;

export const RoleToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 0 24px;
  // Part of the fixed header — never compress it when the middle band overflows.
  flex-shrink: 0;
`;

export const RoleToggleLabel = styled.span<{ 'data-active'?: boolean }>`
  font-family: 'SF Pro', sans-serif;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.04px;
  font-weight: ${({ 'data-active': active }): number => (active ? 700 : 400)};
  color: #1c2024;
  transition: font-weight 0.15s ease;
`;

// Keeps the MUI Switch track blue in BOTH states (default MUI greys the track when
// unchecked). Only the white thumb slides between Validator (left) and Approver (right),
// so the control never looks disabled/greyed. Scoped to this wrapper — the shared Toggle
// component is untouched.
export const RoleToggleSwitch = styled.div`
  display: flex;
  align-items: center;

  .MuiSwitch-track {
    background-color: #1976d2 !important;
    opacity: 1 !important;
  }

  .MuiSwitch-thumb {
    color: #ffffff;
  }
`;

export const InputContainer = styled.div`
  background: rgba(0, 71, 241, 0.07);
  border: 2px solid #ffffff;
  border-radius: 4px;
  height: 28px;
  display: flex;
  align-items: center;
  padding: 0 4px;
  width: 100%;
  box-sizing: border-box;
`;

export const DateInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  background: rgba(0, 71, 241, 0.07);
  border: 2px solid #ffffff;
  border-radius: 4px;
  height: 28px;
  padding: 0 4px;
  box-sizing: border-box;
`;

export const SanctionsDateInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 180px;
  background: rgba(0, 71, 241, 0.07);
  border: 2px solid #ffffff;
  border-radius: 4px;
  height: 28px;
  padding: 0 4px;
  box-sizing: border-box;
`;

export const SanctionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

export const SanctionsContentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
`;

export const ResetLink = styled.button`
  background: none;
  border: none;
  color: #1976d2;
  font-size: 15px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;

  &:hover {
    color: #1565c0;
  }
`;

export const PayeeDetailsSection = styled.div`
  margin-bottom: 24px;
  border: 1px solid #1976d2;
  border-radius: 6px;
  padding: 16px;
  min-width: 0;
`;

export const AccountDetailsSection = styled.div`
  margin-bottom: 24px;
  border: 1px solid #1976d2;
  border-radius: 6px;
  padding: 16px;
  min-width: 0;
`;

export const AccountDetailsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const AccountHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  // Keep the Copy/Delete icons pinned to the right at any screen size, never shrinking
  // or wrapping under a long section title.
  margin-left: auto;
  flex-shrink: 0;
`;

export const AddAccountButtonContainer = styled.div`
  margin-top: 16px;
  margin-bottom: 24px;
  display: flex;
  justify-content: flex-start;
  border: 1px solid #1976d2;
  border-radius: 6px;
  padding: 16px;
  overflow-x: auto;
  min-width: 0;
`;

// Scrolling middle band: TopSection + Payee Details + Account sections + Add Account. Sits between
// the fixed header (role toggle only) and the fixed BottomActions footer, and is the only part of
// the Verification tab that scrolls vertically. The TopSection scrolls away with the content — only
// the role toggle stays pinned. overflow-x:auto keeps wide tables horizontally scrollable.
export const MiddleScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
`;

export const BottomActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  // Pinned footer: never scrolls with the content above it.
  flex-shrink: 0;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
  background: white;
`;

export const BottomActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  button {
    font-family:
      'SF Pro',
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      Roboto,
      'Helvetica Neue',
      Arial,
      sans-serif;
  }
`;

export const SectionHeader = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
`;

export const AccountControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 18px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const AccountControlsLeft = styled.div`
  display: flex;
  align-items: end;
  gap: 18px;
  flex-wrap: wrap;
`;

// Trailing controls (e.g. Private Account) — pinned to the right at any screen size.
// margin-left:auto keeps them right-aligned even when AccountControls wraps on narrow widths.
export const AccountControlsRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: auto;
`;

export const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const AmountReceivedContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

export const ValidationTypeDropdownItem = styled(DropdownItem)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;
  letter-spacing: 0.1px;
  color: var(--transparent-black-600);
`;

// Scrolls only the table horizontally, so the section header (title, Copy/Delete) and the
// controls row (Validation Type, Private Account) stay put while a wide table scrolls.
export const TableScrollContainer = styled.div`
  overflow-x: auto;
  min-width: 0;
`;

export const DataTable = styled.table`
  width: auto;
  min-width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  border: 2px solid #ffffff;
  border-radius: 6px;
  overflow: hidden;
  background: white;
`;

export const TableHeader = styled.thead`
  background: #edf2fe;
`;

export const TableHeaderCell = styled.th<{ 'data-column'?: string }>`
  padding: 4px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #1c2024;
  border-bottom: 2px solid #ffffff;
  border-right: 2px solid #ffffff;
  box-shadow: inset 0 -1px 0 0 #ededed;

  &:last-child {
    border-right: none;
  }

  &[data-column='dataPoint'] {
    width: 156px;
    min-width: 156px;
  }

  &[data-column='vendor'] {
    width: 288px;
    min-width: 288px;
    max-width: 288px;
  }

  &[data-column='evidence'] {
    width: 288px;
    min-width: 288px;
    max-width: 288px;
  }

  &[data-column='automatedMatching'] {
    width: 112px;
    min-width: 112px;
  }

  &[data-column='manualProcess'],
  &[data-column='finalReview'] {
    min-width: 180px;
  }
`;

export const TableBody = styled.tbody``;

export const TableRow = styled.tr<{
  'data-is-parent'?: boolean;
  'data-is-child'?: boolean;
  'data-has-child-following'?: boolean;
  'data-is-last-sub-row'?: boolean;
}>`
  background: #ffffff;
  transition: background-color 0.2s ease;

  &[data-is-child='true'] {
    background: #ffffff;
    border-left: 3px solid #ffffff;
  }

  &:hover {
    background: #f5f5f5;

    &[data-is-child='true'] {
      background: #f5f5f5;
    }
  }
`;

export const TableCell = styled.td<{
  'data-is-child'?: boolean;
  'data-has-child-following'?: boolean;
  'data-is-last-sub-row'?: boolean;
  'data-is-last-in-group'?: boolean;
}>`
  padding: 4px 12px;
  font-size: 14px;
  color: #1c2024;
  border-bottom: 2px solid #ffffff;
  border-right: 2px solid #ffffff;
  background: inherit;
  vertical-align: top;
  position: relative;
  box-shadow: inset 0 -1px 0 0 #ededed;

  &:last-child {
    border-right: none;
  }

  /* Remove border if parent row has child row following */
  &[data-has-child-following='true'] {
    border-bottom: none;
    box-shadow: none;
  }

  /* Remove border for child rows unless it's the last in group */
  &[data-is-child='true']:not([data-is-last-in-group='true']) {
    border-bottom: none;
    box-shadow: none;
  }

  &[data-column='dataPoint'] {
    background: #f6f8fd;
    font-weight: 700;
    width: 156px;
    min-width: 156px;
  }

  &[data-column='vendor'] {
    background: #ffffff;
    width: 288px;
    min-width: 288px;
    max-width: 288px;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  &[data-column='evidence'] {
    background: #fefefe;
    width: 288px;
    min-width: 288px;
    max-width: 288px;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  &[data-column='automatedMatching'] {
    width: 112px;
    min-width: 112px;
    padding: 0;
    vertical-align: middle;
    text-align: center;
  }

  &[data-column='manualProcess'],
  &[data-column='finalReview'] {
    min-width: 180px;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    vertical-align: middle;
    // Display-only uppercasing of the review values. Deliberately CSS and not a transform on the
    // stored value: the NOT_RELEVANT sentinel is compared with === in several places, and
    // parseLegalIdentifierString resolves a legal identifier's type name back to its typeId at save
    // time — uppercasing either would break the match.
    text-transform: uppercase;
  }

  /* Child row styling - remove border between rows in same group */
  &[data-is-child='true'] {
    border-bottom: none;
    box-shadow: none;

    &[data-column='dataPoint'] {
      background: #f6f8fd;
      font-weight: 400;
      color: #1c2024;
    }

    &[data-column='vendor'] {
      background: #ffffff;
    }

    &[data-column='evidence'] {
      background: #ffffff;
    }

    /* Add border back for the last row in a group (separates groups) */
    &[data-is-last-in-group='true'] {
      border-bottom: 2px solid #ffffff;
      box-shadow: inset 0 -1px 0 0 #ededed;
    }

    /* Add border back only for the last sub-row in an expanded group */
    &[data-is-last-sub-row='true'] {
      border-bottom: 2px solid #ffffff;
      box-shadow: inset 0 -1px 0 0 #ededed;
    }
  }
`;
